import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { LOCAL__ui_generation_set_hovered_button_grid2d_dimension } from '@/state/ui/generation/LOCAL__ui_generation_set_hovered_button_grid2d_dimension';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__string_hash_ml_asset } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/hash/FIELD_PRIMITIVE__string_hash_ml_asset';
import { forwardRef, useState } from 'react';
import { ENUM__page_generation_layout } from '../../_enums/ENUM__page_generation_layout';
import { HOOK__ml_asset_name_and_version } from '../../_hooks/HOOK__ml_asset_name_and_version';
import { G_HOOK__graph_field_hashed_for_hover } from '../_base/_hooks/G_HOOK__graph_field_hashed_for_hover';
import { G_BUTTON__field_string_list_item_change_visibility } from '../primitive_string/_buttons/G_BUTTON__field_string_list_item_change_visibility';

interface PROPS {
  field: FIELD_PRIMITIVE__string_hash_ml_asset;
}

export const G_UI_FIELD_PRIMITIVE__string_ml_asset_hash_list_item = forwardRef<HTMLDivElement, PROPS>(({ field }, ref) => {
  /*
   * STORE
   */

  const dispatch = use_dispatch();

  const { g_general__ui_disabled, g_hovered_grid2d_column, g_hovered_grid2d_row, g_general__layout } = use_slice_ui([
    'g_general__ui_disabled',
    'g_hovered_grid2d_column',
    'g_hovered_grid2d_row',
    'g_general__layout',
  ]);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { generation_current } = use_slice_generation(['generation_current']);

  /*
   * STATE
   */
  let { graph } = generation_current.tool;

  let field_from_graph = graph.field__get_primitive(field.meta);

  const { name, version } = HOOK__ml_asset_name_and_version({ hash: field.get_value() });
  const { name: name_from_graph, version: version_from_graph } = HOOK__ml_asset_name_and_version({ hash: field_from_graph.get_value() });

  const field_hashed = G_HOOK__graph_field_hashed_for_hover(field);

  const [is_mouse_over, set_is_mouse_over] = useState(false);

  let field_value_is_in_range = field.get_value_range().includes(field.get_value());

  /*
   * EFFECTS
   */

  /*
   * EVENTS
   */

  async function onClick() {
    if (is_disabled()) return;

    let field_from_graph = graph.field__get_primitive(field.meta);

    if (field.get_value() == field_from_graph.get_value()) return;

    /*
     *
     */
    let field_new = field.clone();

    field_new.add_value_to_values_range(field.get_value());

    /*
     *
     */

    await UTILS__graph_data.update_field(graph, field_new);
  }

  function onMouseEnter() {
    set_is_mouse_over(true);

    if (g_general__layout != ENUM__page_generation_layout.OUTPUTS) return;

    if (field.meta.grid2d__is_column) {
      dispatch(
        LOCAL__ui_generation_set_hovered_button_grid2d_dimension({ g_hovered_grid2d_column: field_hashed, g_hovered_grid2d_dimension_from_grid: false }),
      );
    } else if (field.meta.grid2d__is_row) {
      dispatch(LOCAL__ui_generation_set_hovered_button_grid2d_dimension({ g_hovered_grid2d_row: field_hashed, g_hovered_grid2d_dimension_from_grid: false }));
    }
  }

  function onMouseLeave() {
    set_is_mouse_over(false);

    if (g_general__layout != ENUM__page_generation_layout.OUTPUTS) return;

    dispatch(LOCAL__ui_generation_set_hovered_button_grid2d_dimension());
  }

  /*
   * METHODS
   */
  function get_field_hashed() {
    let graph_clone = generation_current.tool.graph.clone();

    graph_clone.field__replace(field);

    graph_clone.rehash();

    let out = graph_clone.field__get_by_id(field.meta.field_id);
    assert_instanceof(out, FIELD_PRIMITIVE__string_hash_ml_asset);

    return out;
  }

  function get_class_name_grid2d_dimension(grid2d_dimension: FIELD__base) {
    if (!(grid2d_dimension instanceof FIELD_PRIMITIVE__string_hash_ml_asset)) return '';
    if (grid2d_dimension.meta.field_id != field.meta.field_id) return '';
    if (grid2d_dimension.get_value() != field.get_value()) return '';

    return 'g_sidebar_list_item_hovered !border-solid';
  }

  function get_is_used_in_generation() {
    let field_from_graph = graph.field__get_primitive(field.meta);

    return field_from_graph.get_value() == field.get_value();
  }

  function is_disabled() {
    return g_general__ui_disabled || !current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE);
  }

  /*
   *
   */

  function render_version() {
    if (version == ENUM__string_id_empty.ML_ASSET_VERSION) return null;

    return <div className="w-12 max-w-12 truncate">{version}</div>;
  }
  /*
   * HTML
   */

  return (
    <>
      <div
        ref={ref}
        key={`${field.meta.field_id}_${field.get_value()}`}
        {...{ onClick, onMouseEnter, onMouseLeave }}
        className={`
        g_sidebar_list_item gap-2 pl-4 w-full
        ${get_is_used_in_generation() && 'g_sidebar_list_item_hovered border-dashed font-bold'}

        ${field_value_is_in_range ? '' : 'opacity-50'}
        
        ${get_class_name_grid2d_dimension(g_hovered_grid2d_column)}
        ${get_class_name_grid2d_dimension(g_hovered_grid2d_row)}
        `}
      >
        <div className="w-28 max-w-28 truncate">{name}</div>
        {render_version()}
        {is_mouse_over && <G_BUTTON__field_string_list_item_change_visibility {...{ field }} />}
      </div>
    </>
  );
});
